import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AssistantTurnStepSchema, {
  AssistantTurnStepJSON,
  AssistantTurnStepKind,
  AssistantTurnStepOutcome,
  AssistantTurnStepParams,
} from "ai-assistant/turn-steps/assistant-turn-step-schema";

/**
 * One thing the assistant did on the way to an answer, which the panel lists beneath it (#7284):
 * "Searched work records", "Checked citations".
 */
export default class AssistantTurnStep {
  public static create(values: AssistantTurnStepParams): AssistantTurnStep {
    return new AssistantTurnStep(AssistantTurnStepSchema.validate(values));
  }

  public static fromJSON(json: AssistantTurnStepJSON): AssistantTurnStep {
    if (isNullOrUndefined(json)) return json;
    return new AssistantTurnStep(AssistantTurnStepSchema.deserialize(json));
  }

  public readonly kind: AssistantTurnStepKind;
  public readonly label: string;
  public readonly outcome: AssistantTurnStepOutcome;
  public readonly count: number | undefined;

  public constructor(params: AssistantTurnStepParams) {
    this.kind = params.kind;
    this.label = params.label;
    this.outcome = params.outcome;
    this.count = params.count;
  }

  /**
   * The step as one phrase: the server's label, how many times where one capability ran more
   * than once, and how it ended where it did not end well. A verification label already says
   * how many citations it checked.
   */
  public describe(): string {
    const repeated = this.kind === AssistantTurnStepKind.Capability && (this.count ?? 1) > 1;
    const counted = repeated ? `${this.label} (${this.count} times)` : this.label;
    switch (this.outcome) {
      case AssistantTurnStepOutcome.Refused:
        // Neutral on purpose: a capability refuses for a missing or invalid argument as often
        // as for a permission, and the model's answer already says which.
        return `${counted} (could not be done)`;
      case AssistantTurnStepOutcome.Failed:
        return `${counted} (did not finish)`;
      default:
        return counted;
    }
  }

  public toJSON(): AssistantTurnStepJSON {
    return AssistantTurnStepSchema.serialize(this);
  }

  public isEqualTo(other: AssistantTurnStep): boolean {
    return JSON.stringify(this.toJSON()) === JSON.stringify(other.toJSON());
  }

  public toString(): string {
    return this.describe();
  }
}
